import {
  RoutineType,
  SortDirectionEnum,
  useCustomQuery,
  useFindByQuery,
  WorkflowRoutineConfig,
} from '@gripp/shared-logic';
import { useMemo } from 'react';
import {
  formatResponseValue,
  getResponseKey,
} from '../../../new-routines/components/routineExportFormat';

export type DataPlateColumns = {
  // Column descriptors to append to an export's headers.
  dataPlateHeaders: { label: string; key: string }[];
  // Per-asset cell values keyed by the header keys above.
  valuesByAssetId: Record<string, Record<string, unknown>>;
  // True while the data needed to build the columns is still being fetched.
  // Consumers must hold off building rows until this is false.
  isLoading: boolean;
};

const EMPTY: DataPlateColumns = {
  dataPlateHeaders: [],
  valuesByAssetId: {},
  isLoading: false,
};

/**
 * Builds the "data plate" columns for the Add-all-asset-info export option.
 *
 * A data plate routine is any workspace routine flagged `config.oneTimeOnly`.
 * For each such routine this hook pulls each asset's latest non-draft
 * submission and flattens its answers into columns (one per question for a
 * Workflow routine, a single column otherwise), prefixed with the routine name.
 * Consumers append `dataPlateHeaders` to their headers and merge
 * `valuesByAssetId[assetId]` into each row — but only once `isLoading` is false.
 *
 * Only routines that actually have submission data get a column — empty ones
 * are omitted. Pass `assetId` to scope that "has data" check to a single asset
 * (per-asset exports); omit it to count any asset in the workspace.
 */
export const useDataPlateColumns = (
  workspaceId: string | undefined,
  enabled: boolean,
  assetId?: string
): DataPlateColumns => {
  // All workspace routines (with config) → keep the one-time ("data plate") ones.
  const { data: routinesData, isLoading: routinesLoading } = useFindByQuery({
    query: GET_WORKSPACE_ROUTINES,
    modelName: 'WorkspaceRoutine',
    variables: { filter: { workspace: { eq: workspaceId } } },
    options: { enabled: enabled && !!workspaceId },
  });

  const dataPlateRoutines = useMemo(
    () =>
      (routinesData?.items ?? []).filter(
        (routine: { config?: { oneTimeOnly?: boolean } }) =>
          routine.config?.oneTimeOnly === true
      ),
    [routinesData]
  );

  const dataPlateRoutineIds = useMemo(
    () => dataPlateRoutines.map((routine: { id: string }) => routine.id),
    [dataPlateRoutines]
  );

  const currentVersionIds = useMemo(
    () =>
      dataPlateRoutines
        .map((routine: { currentVersion?: { id?: string } }) =>
          routine.currentVersion?.id
        )
        .filter((id: string | undefined): id is string => !!id),
    [dataPlateRoutines]
  );

  // Current version configs supply the human-readable column labels.
  const { data: versionData, isLoading: versionsLoading } = useFindByQuery({
    query: GET_DATA_PLATE_VERSIONS,
    modelName: 'workspaceRoutineVersion',
    variables: { filter: { id: { in: currentVersionIds } } },
    options: { enabled: enabled && currentVersionIds.length > 0 },
  });

  const versionConfigById = useMemo(() => {
    const map = new Map<string, { config?: WorkflowRoutineConfig }>();
    versionData?.items?.forEach(
      (version: { id: string; config?: WorkflowRoutineConfig }) =>
        map.set(version.id, version)
    );
    return map;
  }, [versionData]);

  // Latest non-draft submissions for the data-plate routines, across the workspace.
  const { data: submissionData, isLoading: submissionsLoading } = useCustomQuery({
    query: GET_DATA_PLATE_SUBMISSIONS,
    cacheKey: ['dataPlateSubmissions', workspaceId, dataPlateRoutineIds],
    variables: {
      filter: {
        routine: { in: dataPlateRoutineIds },
        isDraft: { ne: true },
      },
      orderBy: { field: 'createdAt', order: SortDirectionEnum.Asc },
    },
    options: {
      enabled: enabled && dataPlateRoutineIds.length > 0,
      gcTime: 10000,
      staleTime: 10000,
    },
  });

  // Still loading if the routine list is in flight, or there are data-plate
  // routines whose submissions/versions haven't arrived yet. Closing this race
  // is what keeps consumers from building rows before the columns are ready.
  const isLoading =
    enabled &&
    (routinesLoading ||
      (dataPlateRoutineIds.length > 0 &&
        (!submissionData || submissionsLoading)) ||
      (currentVersionIds.length > 0 && (!versionData || versionsLoading)));

  return useMemo(() => {
    if (!enabled || dataPlateRoutines.length === 0) {
      return { ...EMPTY, isLoading };
    }

    // Keep only the latest non-draft submission per (asset, routine). Computing
    // this first lets us drop one-time routines that have no submission data
    // before building columns, so the export never sprouts empty columns.
    const items: any[] = submissionData?.findAssetRoutines?.items ?? [];
    const supersededIds = new Set(
      items.map((item) => item.revisionOf?.id).filter(Boolean)
    );
    const latestByAsset = new Map<string, Map<string, any>>();
    items.forEach((item) => {
      if (supersededIds.has(item.id)) return;
      const itemAssetId = item.asset?.id;
      const routineId = item.routine?.id;
      if (!itemAssetId || !routineId) return;

      let byRoutine = latestByAsset.get(itemAssetId);
      if (!byRoutine) {
        byRoutine = new Map();
        latestByAsset.set(itemAssetId, byRoutine);
      }
      const existing = byRoutine.get(routineId);
      if (
        !existing ||
        new Date(item.createdAt).getTime() >
          new Date(existing.createdAt).getTime()
      ) {
        byRoutine.set(routineId, item);
      }
    });

    // Which one-time routines actually have data. When an assetId is supplied
    // (per-asset export) only that asset's submissions count; otherwise any
    // asset in the workspace counts.
    const routineIdsWithData = new Set<string>();
    const collectRoutineIds = (byRoutine?: Map<string, any>) =>
      byRoutine?.forEach((submission, routineId) => {
        if (submission.data) routineIdsWithData.add(routineId);
      });
    if (assetId) {
      collectRoutineIds(latestByAsset.get(assetId));
    } else {
      latestByAsset.forEach((byRoutine) => collectRoutineIds(byRoutine));
    }

    type ColumnSpec =
      | { kind: 'workflow'; questions: { id: string; key: string }[] }
      | { kind: 'single'; key: string };

    const headers: { label: string; key: string }[] = [];
    const columnSpecByRoutineId = new Map<string, ColumnSpec>();

    // Only build columns for routines that have data (scoped above).
    dataPlateRoutines
      .filter((routine: { id: string }) => routineIdsWithData.has(routine.id))
      .forEach(
        (routine: {
          id: string;
          name?: string;
          type?: RoutineType;
          config?: { title?: string };
          currentVersion?: { id?: string };
        }) => {
          const routineName =
            routine.config?.title ?? routine.name ?? 'Data Plate';
          const version = routine.currentVersion?.id
            ? versionConfigById.get(routine.currentVersion.id)
            : undefined;

          if (routine.type === RoutineType.Workflow && version?.config?.pages) {
            const questions = version.config.pages
              .flatMap((page) => page.questions ?? [])
              .sort((a, b) => a.order - b.order)
              .map((question) => ({
                id: question.id,
                key: `dataplate:${routine.id}:${question.id}`,
                label: `${routineName} · ${question.instructions ?? ''}`.trim(),
              }));

            questions.forEach((question) =>
              headers.push({ label: question.label, key: question.key })
            );
            columnSpecByRoutineId.set(routine.id, {
              kind: 'workflow',
              questions: questions.map(({ id, key }) => ({ id, key })),
            });
          } else {
            const key = `dataplate:${routine.id}`;
            headers.push({ label: routineName, key });
            columnSpecByRoutineId.set(routine.id, { kind: 'single', key });
          }
        }
      );

    const valuesByAssetId: Record<string, Record<string, unknown>> = {};
    latestByAsset.forEach((byRoutine, rowAssetId) => {
      const row: Record<string, unknown> = {};
      byRoutine.forEach((submission, routineId) => {
        const spec = columnSpecByRoutineId.get(routineId);
        if (!spec) return;

        if (spec.kind === 'workflow') {
          const responsesByQuestionId = new Map<string, any>();
          (submission.data?.responses ?? []).forEach((response: any) =>
            responsesByQuestionId.set(response.questionId, response)
          );
          spec.questions.forEach((question) => {
            const response = responsesByQuestionId.get(question.id);
            if (response) {
              row[question.key] = formatResponseValue(
                response,
                getResponseKey(response)
              );
            }
          });
        } else if (submission.data) {
          row[spec.key] = formatResponseValue(
            submission.data,
            getResponseKey(submission.data)
          );
        }
      });
      valuesByAssetId[rowAssetId] = row;
    });

    return { dataPlateHeaders: headers, valuesByAssetId, isLoading };
  }, [
    enabled,
    dataPlateRoutines,
    versionConfigById,
    submissionData,
    isLoading,
    assetId,
  ]);
};

const GET_WORKSPACE_ROUTINES = `
  query GetWorkspaceRoutines($filter: WorkspaceRoutineFilter) {
    findWorkspaceRoutines(filter: $filter) {
      items {
        id
        name
        type
        config
        currentVersion {
          id
        }
      }
    }
  }
`;

const GET_DATA_PLATE_VERSIONS = `
  query FindWorkspaceRoutineVersions($filter: WorkspaceRoutineVersionFilter) {
    findWorkspaceRoutineVersions(filter: $filter) {
      items {
        id
        type
        config
      }
    }
  }
`;

const GET_DATA_PLATE_SUBMISSIONS = `
  query FindAssetRoutines($filter: AssetRoutineFilter, $orderBy: OrderByInput) {
    findAssetRoutines(filter: $filter, orderBy: $orderBy) {
      items {
        id
        asset {
          id
        }
        routine {
          id
        }
        createdAt
        revisionOf {
          id
        }
        data
      }
    }
  }
`;
